import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Info, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { renderToStaticMarkup } from 'react-dom/server';
import { FaExchangeAlt, FaMapMarkerAlt, FaPlane, FaRoute, FaTaxi, FaTrain } from 'react-icons/fa';
import { MdFlag } from 'react-icons/md';
import { MapContainer, Marker, Polyline, Popup, TileLayer, Tooltip, useMap } from 'react-leaflet';
import './RouteMapModal.css';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom Icons
const createCustomIcon = (type) => {
    let icon;
    let color = '#475569';
    let size = '12px';
    const isMainPoint = type === 'start' || type === 'end' || type === 'stopover';

    if (type === 'flight') {
        icon = <FaPlane />;
        color = '#6366F1';
    } else if (type === 'train') {
        icon = <FaTrain />;
        color = '#ff5b15';
    } else if (type === 'cab' || type === 'transfer') {
        icon = <FaTaxi />;
        color = '#10B981';
    } else if (type === 'start') {
        icon = <FaMapMarkerAlt />;
        color = '#10B981'; // Green for Start
        size = '18px';
    } else if (type === 'end') {
        icon = <MdFlag />;
        color = '#DC2626';
        size = '18px';
    } else if (type === 'stopover') {
        icon = <FaExchangeAlt />;
        color = '#8b5cf6'; // Vibrant Purple for stopover stations
        size = '16px';
    } else {
        icon = <FaMapMarkerAlt />;
    }

    const containerSize = isMainPoint ? '30px' : '20px';

    const iconHtml = renderToStaticMarkup(
        <div
            className={`custom-icon-inner ${type === 'stopover' ? 'stopover-glow' : ''}`}
            style={{
                color: color,
                fontSize: size,
                backgroundColor: 'white',
                borderRadius: '50%',
                width: containerSize,
                height: containerSize,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                border: `${isMainPoint ? '2.5px' : '1.5px'} solid ${color}`,
                transition: 'all 0.3s ease'
            }}
        >
            {icon}
        </div>
    );

    const LSize = isMainPoint ? 32 : 22;
    return L.divIcon({
        html: iconHtml,
        className: 'custom-map-icon',
        iconSize: [LSize, LSize],
        iconAnchor: [LSize / 2, LSize / 2] // Center the icon
    });
};

const FitBounds = ({ points }) => {
    const map = useMap();
    useEffect(() => {
        if (points && points.length > 0 && map) {
            try {
                const bounds = L.latLngBounds(points);
                if (map.getContainer()) {
                    map.flyToBounds(bounds, {
                        padding: [50, 50], // More spacious padding
                        duration: 0.5,       // Snappier transition (2s instead of 3s)
                        easeLinearity: 1.25,
                        maxZoom: 13        // Prevent zooming too deep on short routes
                    });
                }
            } catch (err) {
                console.warn("Map fitBounds failed:", err);
            }
        }
    }, [points, map]);
    return null;
};



const RouteMapModal = ({ isOpen, onClose, routeData, title, searchMetadata }) => {
    const [mapData, setMapData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        const fetchMapData = async () => {
            if (!routeData) return;

            setLoading(true);
            setError(null);

            try {
                // Construct the payload as per the API requirements
                const payload = {
                    route_obj: {
                        status: 200,
                        data: {
                            routes: [routeData],
                            ...(searchMetadata || {})
                        }
                    }
                };

                const response = await fetch('https://hh183pxn0f.execute-api.ap-south-1.amazonaws.com/development/maps', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });

                if (!response.ok) {
                    const errorJson = await response.json().catch(() => ({}));
                    throw new Error(errorJson.body?.error || errorJson.error || `Error ${response.status}: Failed to fetch map data`);
                }

                const data = await response.json();

                // The API returns an array of route objects
                if (Array.isArray(data) && data.length > 0) {
                    setMapData(data[0]);
                } else if (data && data.segments) {
                    // Fallback for single object response
                    setMapData(data);
                } else {
                    throw new Error('Invalid map data received from server');
                }
            } catch (err) {
                console.error('RouteMapModal API Error:', err);
                setError(err.message);
                setMapData(null);
            } finally {
                setLoading(false);
            }
        };

        if (isOpen) {
            fetchMapData();
        } else {
            setMapData(null);
            setError(null);
        }
    }, [isOpen, routeData, retryCount]);

    const handleRetry = () => setRetryCount(prev => prev + 1);

    if (!isOpen) return null;

    const allPoints = [];
    if (mapData?.segments) {
        mapData.segments.forEach(seg => {
            seg.nodes.forEach(node => {
                // Ensure we don't include [0,0] in the map bounds to avoid extreme zoom-outs
                const isZero = node.lat === 0 && node.lng === 0;
                if (node.lat !== undefined && node.lng !== undefined && !isZero) {
                    allPoints.push([node.lat, node.lng]);
                }
            });
        });
    }

    return createPortal(
        <div className="route-map-modal-overlay" onClick={onClose}>
            <div className="route-map-modal-content" onClick={e => e.stopPropagation()}>
                <div className="route-map-modal-header">
                    <div className="header-info">
                        <div className="modal-title-container">
                            <div className="route-icon-box">
                                <FaRoute size={28} />
                            </div>
                            <div className="title-group" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <h3>
                                    {(() => {
                                        if (!mapData?.segments) return title || 'Journey Route Map';
                                        const startNode = mapData.segments[0].nodes[0];
                                        const lastSegNodes = mapData.segments[mapData.segments.length - 1].nodes;
                                        const endNode = lastSegNodes[lastSegNodes.length - 1];

                                        const startCityText = `${startNode.name}${startNode.code ? ` (${startNode.code})` : ''}`;
                                        const endCityText = `${endNode.name}${endNode.code ? ` (${endNode.code})` : ''}`;

                                        // Get unique transfer cities (excluding start and end points)
                                        const viaCities = mapData.segments.slice(0, -1).map(seg => {
                                            const lastNode = seg.nodes[seg.nodes.length - 1];
                                            return lastNode.name;
                                        });

                                        // Filter unique names and limit to 2
                                        const uniqueVia = [...new Set(viaCities)].slice(0, 2);
                                        const viaText = uniqueVia.length > 0 ? ` (Via ${uniqueVia.join(', ')})` : '';

                                        return (
                                            <>
                                                {startCityText} to {endCityText}
                                                {viaText && <span className="title-via">{viaText}</span>}
                                            </>
                                        );
                                    })()}
                                </h3>
                                {(() => {
                                    // 1. Prioritize pre-calculated duration from routeData (matches Cards)
                                    const rawDuration = routeData?.duration ||
                                        routeData?.duration_text ||
                                        routeData?.total_duration ||
                                        routeData?.totalDuration ||
                                        routeData?.travel_time;

                                    // 2. Exact logic from TravelResultsList.jsx to ensure consistency
                                    const calculateExactDuration = () => {
                                        const nodes = routeData?.nodes || [];
                                        if (nodes.length === 0) return null;

                                        let totalMins = 0;
                                        let hasValidData = true;

                                        nodes.forEach((node, index) => {
                                            const seg = node.segment || {};
                                            const dur = parseInt(seg.duration_mins);
                                            if (!isNaN(dur)) {
                                                totalMins += dur;
                                            } else {
                                                hasValidData = false;
                                            }

                                            if (index < nodes.length - 1) {
                                                const nextSeg = nodes[index + 1].segment || {};
                                                const arrivalTime = new Date(seg.dest_arr_dt);
                                                const departureTime = new Date(nextSeg.origin_dep_dt);

                                                if (!isNaN(arrivalTime.getTime()) && !isNaN(departureTime.getTime())) {
                                                    const diffMins = Math.floor((departureTime - arrivalTime) / (1000 * 60));
                                                    if (diffMins > 0) totalMins += diffMins;
                                                } else {
                                                    hasValidData = false;
                                                }
                                            }
                                        });

                                        if (hasValidData && totalMins > 0) {
                                            return `${Math.floor(totalMins / 60)}h ${totalMins % 60}m`;
                                        }

                                        // Fallback like the Cards
                                        try {
                                            const firstSeg = nodes[0].segment;
                                            const lastSeg = nodes[nodes.length - 1].segment;
                                            const diffTime = new Date(lastSeg.dest_arr_dt) - new Date(firstSeg.origin_dep_dt);
                                            if (!isNaN(diffTime) && diffTime > 0) {
                                                return `${Math.floor(diffTime / (1000 * 60 * 60))}h ${Math.floor((diffTime / (1000 * 60)) % 60)}m`;
                                            }
                                        } catch (e) { }
                                        return null;
                                    };

                                    const displayDuration = rawDuration || calculateExactDuration();

                                    if (displayDuration) return (
                                        <div className="duration-chip" style={{
                                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                                            background: '#fff7ed', color: '#c2410c', padding: '3px 10px',
                                            borderRadius: '50px', fontSize: '0.72rem', fontWeight: 700,
                                            border: '1px solid #ffedd5', marginTop: '4px', alignSelf: 'flex-start'
                                        }}>
                                            <span style={{ opacity: 0.7, fontWeight: 600 }}>Total Duration:</span>
                                            <span>{displayDuration}</span>
                                        </div>
                                    );
                                    return null;
                                })()}
                            </div>
                        </div>
                    </div>
                    <button className="close-btn" onClick={onClose}>
                        <X size={24} />
                    </button>
                </div>

                <div className="route-map-modal-body">
                    {loading ? (
                        <div className="map-loading">
                            <div className="spinner"></div>
                            <p>Plotting coordinates...</p>
                        </div>
                    ) : mapData ? (
                        <div className="map-wrapper">
                            <MapContainer
                                key={mapData.route_id || 'initial'}
                                center={allPoints.length > 0 ? allPoints[0] : [23.178, 80.052]}
                                zoom={6}
                                style={{ height: '100%', width: '100%' }}
                                zoomControl={false}
                                zoomSnap={0.1}
                                zoomDelta={0.1}
                                wheelPxPerZoomLevel={100}
                                animate={true}
                            >
                                <TileLayer
                                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                                />

                                {(() => {
                                    const colors = {
                                        train: ['#ff5b15', '#fb923c'], // Dark Orange, Light Orange
                                        flight: ['#6366F1', '#a5b4fc'], // Dark Indigo, Light Indigo 
                                        cab: ['#10B981', '#6ee7b7'],    // Dark Green, Light Green
                                        transfer: ['#10B981', '#6ee7b7']
                                    };

                                    const modeCounters = { train: 0, flight: 0, cab: 0, transfer: 0 };

                                    // Guardrail: Filter out segments that connect to [0, 0]
                                    const validSegments = mapData.segments.filter(seg => {
                                        const lastNode = seg.nodes[seg.nodes.length - 1];
                                        const isLastZero = lastNode?.lat === 0 && lastNode?.lng === 0;
                                        return !isLastZero;
                                    });

                                    return (
                                        <>
                                            {/* 1. Rendering Polylines */}
                                            {validSegments.map((seg, sIdx) => {
                                                const positions = seg.nodes
                                                    .filter(n => !(n.lat === 0 && n.lng === 0))
                                                    .map(n => [n.lat, n.lng]);
                                                const type = seg.type;

                                                // Determine color based on how many times this mode has appeared
                                                const palette = colors[type] || ['#475569', '#94a3b8'];
                                                const color = palette[modeCounters[type] % palette.length];

                                                // Increment the counter for this specific mode
                                                modeCounters[type]++;

                                                return (
                                                    <Polyline
                                                        key={`poly-${sIdx}`}
                                                        positions={positions}
                                                        pathOptions={{
                                                            color: color,
                                                            weight: 4,
                                                            dashArray: '0',
                                                            opacity: 0.8
                                                        }}
                                                    >
                                                        <Popup>
                                                            <div className="popup-content">
                                                                <strong>{seg.type.toUpperCase()}</strong><br />
                                                                {seg.id && <span>ID: {seg.id}<br /></span>}
                                                                {seg.from_code} ➔ {seg.to_code}
                                                            </div>
                                                        </Popup>
                                                    </Polyline>
                                                );
                                            })}

                                            {/* 2. Rendering Labels (Train/Flight/Cab) */}
                                            {validSegments.map((seg, sIdx) => {
                                                if (!seg.nodes || seg.nodes.length < 2) return null;

                                                // Pick the actual middle node from the segment to ensure the label is ALWAYS on the polyline path
                                                const validNodes = seg.nodes.filter(n => !(n.lat === 0 && n.lng === 0));
                                                if (validNodes.length === 0) return null;

                                                // UNIFIED LABEL POSITION LOGIC (SAFE VERSION):
                                                let labelPos;
                                                const vLen = validNodes.length;

                                                if (vLen >= 2) {
                                                    if (vLen === 2) {
                                                        // 2 nodes (mostly Cab/Transfer): geographic midpoint is best
                                                        labelPos = [(validNodes[0].lat + validNodes[1].lat) / 2, (validNodes[0].lng + validNodes[1].lng) / 2];
                                                    } else {
                                                        // many nodes: use middle node center to stay on path
                                                        const midIdx = Math.floor(vLen / 2);
                                                        labelPos = [validNodes[midIdx].lat, validNodes[midIdx].lng];
                                                    }
                                                } else {
                                                    // Only 1 node available: use it as is
                                                    labelPos = [validNodes[0].lat, validNodes[0].lng];
                                                }

                                                const emoji = seg.type === 'flight' ? '✈️' : seg.type === 'train' ? '🚂' : '🚕';
                                                const displayType = seg.type.charAt(0).toUpperCase() + seg.type.slice(1);
                                                const isRedundantId = seg.id && (seg.id.toLowerCase() === seg.type.toLowerCase() || seg.id.toLowerCase() === 'cab');
                                                const displayId = (seg.id && !isRedundantId) ? `#${seg.id}` : '';
                                                const isCabType = seg.type === 'cab' || seg.type === 'transfer';
                                                const distKm = seg.meta?.distance_kms || seg.distance_kms;
                                                const timeMins = seg.meta?.time_mins || seg.time_mins;

                                                // Render pill as the marker icon HTML so entire pill is hoverable
                                                const pillHtml = renderToStaticMarkup(
                                                    <span className={`route-pill-label underlined ${seg.type}`}>{emoji} {displayType} {displayId}</span>
                                                );

                                                return (
                                                    <Marker
                                                        key={`label-${sIdx}`}
                                                        position={labelPos}
                                                        icon={L.divIcon({
                                                            html: pillHtml,
                                                            className: `route-pill-icon ${seg.type}`,
                                                            iconSize: [null, null],
                                                            iconAnchor: [40, 28] // Float above the line
                                                        })}
                                                    >
                                                        <Tooltip direction="top" offset={[0, -15]} className="route-hover-tooltip">
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', padding: '4px', minWidth: '150px' }}>
                                                                <div style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '3px', marginBottom: '2px' }}>
                                                                    <strong style={{ color: seg.type === 'train' ? '#ff5b15' : seg.type === 'flight' ? '#6366F1' : '#059669', fontSize: '14px' }}>
                                                                        {displayId || displayType}
                                                                    </strong>
                                                                </div>
                                                                <div style={{ fontSize: '12px', fontWeight: 600, color: '#1e293b' }}>
                                                                    {seg.nodes[0]?.name} ({seg.from_code}) ➔ {seg.nodes[seg.nodes.length - 1]?.name} ({seg.to_code})
                                                                </div>
                                                                {(distKm || timeMins) && (
                                                                    <div style={{ fontSize: '14px', color: '#64748b', marginTop: '2px' }}>
                                                                        {emoji} {distKm ? `${distKm} km` : ''}{distKm && timeMins ? ' · ' : ''}{timeMins ? `${timeMins} min` : ''}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </Tooltip>
                                                    </Marker>
                                                );
                                            })}

                                            {/* 3. Rendering Markers (Pins/Flags) */}
                                            {(() => {
                                                if (validSegments.length === 0) return null;

                                                const markersMap = new Map();
                                                const genKey = (lat, lng) => `${Number(lat).toFixed(4)},${Number(lng).toFixed(4)}`;

                                                const firstSeg = validSegments[0];
                                                const firstNode = firstSeg.nodes[0];
                                                const startKey = genKey(firstNode.lat, firstNode.lng);

                                                const lastSeg = validSegments[validSegments.length - 1];
                                                const lastNode = lastSeg.nodes[lastSeg.nodes.length - 1];
                                                const endKey = genKey(lastNode.lat, lastNode.lng);

                                                // Build map for intermediate points
                                                validSegments.forEach((seg) => {
                                                    seg.nodes.forEach((n, nIdx) => {
                                                        if (!n || (n.lat === 0 && n.lng === 0)) return;
                                                        const key = genKey(n.lat, n.lng);

                                                        // SKIP Start and End from the intermediate markers array to render them separately on top
                                                        if (key === startKey || key === endKey) return;

                                                        if (!markersMap.has(key)) {
                                                            markersMap.set(key, {
                                                                pos: [n.lat, n.lng],
                                                                node: { ...n },
                                                                type: seg.type,
                                                                isStopover: false,
                                                                inbound: null,
                                                                outbound: null
                                                            });
                                                        }

                                                        const current = markersMap.get(key);
                                                        const isStartOfSegment = nIdx === 0;
                                                        const isEndOfSegment = nIdx === seg.nodes.length - 1;

                                                        // Logic to prioritize & MERGE timings:
                                                        // 1. End of a segment is always our Inbound Arrival
                                                        if (isEndOfSegment) {
                                                            current.node.arr = n.arr;
                                                            current.inbound = seg;
                                                            current.isStopover = true;
                                                            current.type = 'stopover';
                                                        }

                                                        // 2. Start of a segment is always our Outbound Departure
                                                        if (isStartOfSegment) {
                                                            current.node.dep = n.dep;
                                                            current.outbound = seg;
                                                            // If we have an inbound meeting this outbound, it's a stopover station
                                                            if (current.inbound) {
                                                                current.isStopover = true;
                                                                current.type = 'stopover';
                                                            }
                                                        }

                                                        // 3. For intermediate stops, ensure we have basic arr/dep info if not already set
                                                        if (!isStartOfSegment && !isEndOfSegment) {
                                                            if (!current.node.arr) current.node.arr = n.arr;
                                                            if (!current.node.dep) current.node.dep = n.dep;
                                                        }
                                                    });
                                                });

                                                const formatTime = (val) => {
                                                    if (!val) return null;
                                                    if (val.toString().includes('T')) {
                                                        try {
                                                            const d = new Date(val);
                                                            return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                                        } catch (e) { return val; }
                                                    }
                                                    return val;
                                                };

                                                const calculateLayover = (arr, dep) => {
                                                    if (!arr || !dep) return null;
                                                    try {
                                                        const getMin = (t) => {
                                                            if (t.includes('T')) return new Date(t).getTime() / 60000;
                                                            const [h, m] = t.split(':').map(Number);
                                                            return h * 60 + m;
                                                        };
                                                        const diff = getMin(dep) - getMin(arr);
                                                        if (diff <= 0) return null;
                                                        return diff < 60 ? `${diff}m` : `${Math.floor(diff / 60)}h ${diff % 60}m`;
                                                    } catch (e) { return null; }
                                                };

                                                return (
                                                    <>
                                                        {/* Station Tooltips for Intermediate Points (Junctions Only) */}
                                                        {Array.from(markersMap.values())
                                                            .filter(m => {
                                                                if (m.isStopover) return true;
                                                                // Regex to match "JN", "jn", "Junction" patterns
                                                                const junctionPattern = /\bJN\b|\bjn\b|\bJunction\b|\bjunction\b/i;
                                                                return junctionPattern.test(m.node.name);
                                                            })
                                                            .map((m, i) => {
                                                                const arrTime = formatTime(m.node.arr);
                                                                const depTime = formatTime(m.node.dep);
                                                                const layover = calculateLayover(m.node.arr || '', m.node.dep || '');

                                                                return (
                                                                    <Marker key={i} position={m.pos} icon={createCustomIcon(m.type)}>
                                                                        <Tooltip direction="top" offset={[0, -12]} opacity={1} className="station-tooltip">
                                                                            <div className="tooltip-node">
                                                                                <div className={`tooltip-header ${m.isStopover ? 'stopover-header' : ''}`}>
                                                                                    <div className="tooltip-title">{m.node.name} ({m.node.code || 'Transit'})</div>
                                                                                    {m.isStopover && <span className="stopover-badge">Stopover</span>}
                                                                                </div>
                                                                                <div className="tooltip-body">
                                                                                    {(arrTime && depTime) ? (
                                                                                        <div className="layover-container">
                                                                                            <div className="layover-side">
                                                                                                <div className="side-vehicle" style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', marginBottom: '2px' }}>
                                                                                                    {m.inbound?.id ? `${m.inbound.type.charAt(0).toUpperCase() + m.inbound.type.slice(1)} #${m.inbound.id}` : ""}
                                                                                                </div>
                                                                                                <span className="side-label" style={{ opacity: 0.6 }}>Arrival</span>
                                                                                                <span className="side-val">{arrTime}</span>
                                                                                            </div>
                                                                                            <div className="layover-divider"></div>
                                                                                            <div className="layover-side">
                                                                                                <div className="side-vehicle" style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', marginBottom: '2px' }}>
                                                                                                    {m.outbound?.id ? `${m.outbound.type.charAt(0).toUpperCase() + m.outbound.type.slice(1)} #${m.outbound.id}` : ""}
                                                                                                </div>
                                                                                                <span className="side-label" style={{ opacity: 0.6 }}>Departure</span>
                                                                                                <span className="side-val">{depTime}</span>
                                                                                            </div>
                                                                                            {m.isStopover && layover && <div className="layover-duration">Gap: {layover}</div>}
                                                                                        </div>
                                                                                    ) : (
                                                                                        <>
                                                                                            {arrTime && <div className="time-row"><span>Arrival:</span> <strong>{arrTime}</strong></div>}
                                                                                            {depTime && <div className="time-row"><span>Departure:</span> <strong>{depTime}</strong></div>}
                                                                                        </>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </Tooltip>
                                                                    </Marker>
                                                                );
                                                            })}

                                                        {/* EXPLICIT START PIN */}
                                                        <Marker position={[firstNode.lat, firstNode.lng]} icon={createCustomIcon('start')} zIndexOffset={1000}>
                                                            <Tooltip direction="top" offset={[0, -12]} opacity={1} className="station-tooltip">
                                                                <div className="tooltip-node">
                                                                    <div className="tooltip-header"><div className="tooltip-title">{firstNode.name}</div></div>
                                                                    <div className="tooltip-body">
                                                                        {formatTime(firstNode.dep) && <div className="time-row"><span>Departure:</span> <strong>{formatTime(firstNode.dep)}</strong></div>}
                                                                    </div>
                                                                </div>
                                                            </Tooltip>
                                                        </Marker>

                                                        {/* Permanent Label for Start */}
                                                        <Marker
                                                            position={[firstNode.lat, firstNode.lng]}
                                                            icon={L.divIcon({
                                                                html: `<div class="station-name-pill source">${firstNode.name}</div>`,
                                                                className: 'floating-city-label',
                                                                iconSize: [null, null],
                                                                iconAnchor: [0, -15]
                                                            })}
                                                            interactive={false}
                                                        />

                                                        {/* EXPLICIT END FLAG */}
                                                        <Marker position={[lastNode.lat, lastNode.lng]} icon={createCustomIcon('end')} zIndexOffset={1000}>
                                                            <Tooltip direction="top" offset={[0, -12]} opacity={1} className="station-tooltip">
                                                                <div className="tooltip-node">
                                                                    <div className="tooltip-header"><div className="tooltip-title">{lastNode.name}</div></div>
                                                                    <div className="tooltip-body">
                                                                        {formatTime(lastNode.arr) && <div className="time-row"><span>Arrival:</span> <strong>{formatTime(lastNode.arr)}</strong></div>}
                                                                    </div>
                                                                </div>
                                                            </Tooltip>
                                                        </Marker>

                                                        {/* Permanent Label for End */}
                                                        <Marker
                                                            position={[lastNode.lat, lastNode.lng]}
                                                            icon={L.divIcon({
                                                                html: `<div class="station-name-pill dest">${lastNode.name}</div>`,
                                                                className: 'floating-city-label',
                                                                iconSize: [null, null],
                                                                iconAnchor: [0, -15]
                                                            })}
                                                            interactive={false}
                                                        />
                                                    </>
                                                );
                                            })()}
                                        </>
                                    );
                                })()}

                                {mapData.stops?.map((stop, stIdx) => {
                                    const start = [stop.from_node.lat, stop.from_node.lng];
                                    const end = [stop.to_node.lat, stop.to_node.lng];
                                    return (
                                        <Polyline
                                            key={`stop-${stIdx}`}
                                            positions={[start, end]}
                                            pathOptions={{
                                                color: '#10B981',
                                                weight: 3,
                                                dashArray: '0',
                                                opacity: 0.6
                                            }}
                                        >
                                            <Popup>
                                                <div className="popup-content">
                                                    <strong>Transfer ({stop.mode})</strong><br />
                                                    {stop.commute_dist_kms} km, {stop.commute_time_mins} mins
                                                </div>
                                            </Popup>
                                            <Tooltip permanent direction="top" offset={[0, -10]} className="cab-tooltip">
                                                <span>🚕 Cab {stop.commute_dist_kms ? `· ${stop.commute_dist_kms} km` : ''}{stop.commute_time_mins ? ` · ${stop.commute_time_mins} min` : ''}</span>
                                            </Tooltip>
                                        </Polyline>
                                    );
                                })}


                                <FitBounds points={allPoints} />
                            </MapContainer>

                            {(() => {
                                const hasFlight = mapData.segments.some(s => s.type === 'flight');
                                const hasTrain = mapData.segments.some(s => s.type === 'train');
                                const hasCab = mapData.segments.some(s => s.type === 'cab' || s.type === 'transfer') || (mapData.stops && mapData.stops.length > 0);
                                const hasStopover = mapData.segments.length > 1;

                                return (
                                    <div className="map-legend">
                                        {hasFlight && (
                                            <div className="legend-item">
                                                <span className="line flight"></span>
                                                <span>Flight</span>
                                            </div>
                                        )}
                                        {hasTrain && (
                                            <div className="legend-item">
                                                <span className="line train"></span>
                                                <span>Train</span>
                                            </div>
                                        )}
                                        {hasCab && (
                                            <div className="legend-item">
                                                <span className="line transfer"></span>
                                                <span>Cab</span>
                                            </div>
                                        )}
                                        {hasStopover && (
                                            <div className="legend-item">
                                                <div className="dot stopover legend-stopover-icon">
                                                    <FaExchangeAlt size={10} />
                                                </div>
                                                <span>Stopover</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                    ) : (
                        <div className="map-error">
                            <Info size={40} />
                            <p>{error || 'Unable to load map data. Please try again later.'}</p>
                            {error && <button className="retry-btn" onClick={handleRetry}>Retry</button>}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default RouteMapModal;
